// Shared project data — used by the carousel and case study pages.
// Copy for `brief` and `highlights` comes from the client's Descriptions.xlsx.

export const PROJECTS = [
  {
    id: "perx",
    name: "Perx Group",
    url: "https://perx.group",
    tagline: "Customer engagement platform, boldly reimagined",
    live: true,
    img: "/projects/perx-group.png",
    year: "2024",
    skills: ["Web Design", "Web Development", "Portal UX", "CMS"],
    brief:
      "We built a bold, punchy website for Perx Group — the customer engagement platform that turns everyday transactions into real, meaningful relationships. The site perfectly captures their global ambition with strong, confident visuals, while keeping everything refreshingly approachable for enterprise buyers. No stuffy corporate vibes here.",
    highlights: [
      "Custom design system built for easy modular scaling as they grow",
      "Three smart access levels: public-facing site, client portal for partners to manage member requests, and a dedicated member portal",
      "Fully database-driven backend so updating products, offers, and content is lightning fast",
      "Clean SEO-optimised structure with smart content hierarchy",
      "Lightning-fast, beautifully responsive experience on every device",
    ],
    gallery: [],
  },
  {
    id: "tmr",
    name: "TMR Global",
    url: "https://tmrglobal.com",
    tagline: "Enterprise risk, told like a trusted partner",
    live: true,
    img: "/projects/tmr-global.png",
    year: "2024",
    skills: ["Web Design", "Web Development", "SEO", "Marketing Automation"],
    brief:
      "TMR Global helps over 100 regulated organisations manage risk in the modern era. We built a bold, confident website that talks like a trusted partner — never like a dry whitepaper.",
    highlights: [
      "Enterprise-grade, editorial-quality storytelling that actually engages visitors",
      "Strong branding that showcases their global scale",
      "Clear CTAs with automated document delivery, smart follow-ups, and seamless demo booking",
    ],
    gallery: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => `/projects/tmr/${n}.png`),
  },
  {
    id: "qc4",
    name: "QC4 Health",
    url: "https://qc4health.com",
    tagline: "AI-powered clinical governance, made calm",
    live: true,
    img: "/projects/qc4-health.png",
    year: "2024",
    skills: ["Web Design", "UI/UX Design", "Development"],
    brief:
      "QC4 is an AI-powered Clinical Governance platform that helps healthcare organisations stay ahead of compliance with confidence. We built a calm, credible marketing site that turns complex workflows into a clear, relatable value story clinicians and administrators can actually understand and get excited about.",
    highlights: [
      "Trust-first design language that feels professional and reassuring",
      "Beautiful dashboard imagery that mirrors the real product experience",
      "Smart, structured content built for easy future case studies and expansion",
    ],
    gallery: [1, 2, 3, 4, 5, 6].map((n) => `/projects/qc4/${n}.png`),
  },
  {
    id: "opex",
    name: "OpExcellence HK",
    url: "https://opexcellence.hk",
    tagline: "Senior advisors — with a confident site to match",
    live: true,
    img: "/projects/opexcellence.hk.png",
    year: "2024",
    skills: ["Web Design", "Web Development", "Copywriting"],
    brief:
      "OpExcellence needed a website that positioned them as senior advisors, not just another consultancy. We created a confident, minimal site that speaks with authority and turns interested visitors into qualified conversations.",
    highlights: [
      "Confident, senior-partner tone throughout",
      "Smart lead-capture flows designed for high-intent visitors",
      "Lightning-fast page loads and beautifully clean typography",
    ],
    gallery: [1, 2, 3, 4, 5, 6, 7].map((n) => `/projects/opex/${n}.png`),
  },
  {
    id: "pointspal",
    name: "PointsPal",
    url: "https://pointspal.com.hk",
    tagline: "Pay-by-card platform helping HK SMEs unlock points",
    live: false,
    img: "/projects/points-pal.png",
    year: "2025",
    skills: ["Product Design", "Web Design", "Full-stack Development", "PCI DSS"],
    brief:
      "PointsPal is a smart payment service that lets customers pay with credit cards for things that usually can't take cards — helping Hong Kong SMEs free up cashflow while maximising points for their next flight (or holiday). We designed the complete customer experience end-to-end — from the marketing site to a smooth signup flow — with a playful, warm voice that feels like a helpful friend, not a bank.",
    highlights: [
      "Clean, PCI DSS-compliant platform for secure payments",
      "Intuitive client portal for businesses to manage payments easily",
      "Powerful management portal and full reporting dashboards for the PointsPal team, giving complete visibility into everything that's happening",
      "Playful yet trustworthy brand voice and design system ready for future mobile app expansion",
    ],
    gallery: [1, 2, 3, 4, 5, 6, 7].map((n) => `/projects/pointspal/${n}.png`),
  },
  {
    id: "artoo",
    name: "Artoo Digital",
    url: "https://artoo.digital",
    tagline: "AI platform that builds full web apps end-to-end",
    live: false,
    img: "/projects/artoo-digital.png",
    year: "2025",
    skills: ["Product Design", "AI Agents UX", "Development", "Enterprise Integrations"],
    brief:
      "Artoo Digital is a powerful AI platform that builds complex web applications. We helped bring their vision to life by designing a series of 30 specialized AI agents that handle everything — from collecting requirements and designing to development, testing, integration, and launch. It even integrates seamlessly with systems like SAP, Oracle, and ServiceNow. We leaned into a hard-edged, punchy visual style with bold typography, dynamic motion, and a distinctive colour palette that instantly says \"Artoo\" — no scrolling required.",
    highlights: [
      "Distinctive brand-first visual identity that feels futuristic and capable",
      "Delightful micro-interactions on every element",
      "Optimised for social share previews and maximum impact",
    ],
    gallery: [1, 2, 3, 4, 5, 6].map((n) => `/projects/artoo/${n}.png`),
  },
  {
    id: "nostrad",
    name: "Nostra Digital",
    url: "https://nostra.digital",
    tagline: "AI prediction system with a voice-first interface",
    live: false,
    img: "/projects/nostrad-digital.png",
    year: "2025",
    skills: ["Web Design", "AI UX", "Voice Interface", "Subscriptions"],
    brief:
      "Nostra Digital is an impressive AI prediction system that lets users ask any question and sets a team of AI agents to work finding thoughtful answers. It even includes full voice capabilities to make conversations feel natural and productive. Nostra needed a website that felt current and forward-thinking without chasing every trend. We created a compact, confident digital presence with tight editorial writing and a memorable hero moment that stays with you.",
    highlights: [
      "Editorial-quality layout system that makes complex ideas feel clear and engaging",
      "Confident, quiet typography that conveys authority and calm",
      "Smooth user subscription system for seamless access",
    ],
    gallery: [2, 3, 4, 5, 6, 7, 8].map((n) => `/projects/nostrad/${n}.png`),
  },
  {
    id: "getitdone",
    name: "Get It Done HK",
    url: "https://getitdone.hk",
    tagline: "Service marketplace for Hong Kong households",
    live: false,
    img: "/projects/get-it-done-hk.png",
    year: "2025",
    skills: ["Product Design", "Marketplace UX", "Development"],
    brief:
      "Get It Done HK is a service marketplace that connects Hong Kong households with vetted local professionals for everything from repairs to home services. We designed a friendly, low-friction discovery flow and a booking experience that feels more like messaging a helpful friend than filling out a boring form.",
    highlights: [
      "Category-first discovery UX that makes finding the right pro fast and intuitive",
      "Super low-friction booking flow that removes all the usual hassle",
      "Warm, local visual language that feels trustworthy and distinctly Hong Kong",
    ],
    gallery: [1, 2].map((n) => `/projects/getitdone/${n}.png`),
  },
  {
    id: "oakline",
    name: "Oakline Connect",
    url: "#",
    tagline: "AI-powered engagement platform for calls & chats",
    live: false,
    img: "/projects/oakline-connect.png",
    year: "2025",
    skills: ["Product Design", "AI Coaching UX", "Real-time Systems", "Development"],
    brief:
      "Oakline Connect is a smart platform that helps companies manage complex client engagements with confidence. Agents can take calls while AI listens live, giving real-time feedback on compliance, product knowledge, sentiment, and ways to improve call outcomes. It also brings together written queries from email, WhatsApp, and WeChat into one place — with the same AI translating messages in real time, checking for compliance and accuracy, and suggesting strong responses. Smart routing sends conversations to the next available agent, and a supervisor portal automatically brings a supervisor into calls or chats when sentiment drops or compliance risks appear. We built a clean, professional site that clearly communicates the power and peace of mind Oakline delivers.",
    highlights: [
      "Real-time AI coaching and sentiment monitoring during calls",
      "Unified inbox for email, WhatsApp, and WeChat with smart AI assistance",
      "Intelligent supervisor escalation and smart routing",
    ],
    gallery: [1, 2, 3, 4, 5].map((n) => `/projects/oakline/${n}.png`),
  },
  {
    id: "nappy",
    name: "Dollar Nappy Club",
    url: "#",
    tagline: "Subscription nappies delivered on time",
    live: false,
    img: "/projects/dollar-nappy-club.png",
    year: "2025",
    skills: ["E-Commerce Design", "Subscription Flow", "Brand Voice", "Development"],
    brief:
      "Dollar Nappy Club is a subscription brand that makes parents' lives a little easier with reliable, high-quality nappies delivered on time. We built a marketing site and subscription funnel that feels as fresh, warm, and reassuring as the product itself — like getting helpful advice from a trusted friend.",
    highlights: [
      "Warm, parent-first tone throughout the entire experience",
      "Smooth subscription flow with smart upsell opportunities built in naturally",
      "Editorial content system for useful parenting tips and articles",
    ],
    gallery: [1, 2, 3, 4, 5].map((n) => `/projects/nappy/${n}.png`),
  },
  {
    id: "goride",
    name: "Go Ride a Bike",
    url: "#",
    tagline: "Kids' bike lessons, made stress-free",
    live: false,
    img: "/projects/go-ride-a-bike.png",
    year: "2025",
    skills: ["Web Design", "LMS UX", "Parent/School Portals", "Development"],
    brief:
      "Go Ride a Bike makes learning to ride fun and stress-free for kids through school-based lessons. We designed a friendly, low-anxiety website where parents can easily register their children, schools can manage sessions, and franchisees can run their districts smoothly. Parents get a secure area to manage their child's details, watch lesson videos, and see clear progress reports.",
    highlights: [
      "Approachable, friendly voice that puts parents at ease",
      "Optimised for on-the-go mobile use — because busy parents are usually on their phones",
      "Clean, secure portals for parents, schools, and franchisees",
    ],
    gallery: [1, 2, 3, 4, 5, 6, 7].map((n) => `/projects/goride/${n}.png`),
  },
  {
    id: "baseball",
    name: "Baseball Hall",
    url: "#",
    tagline: "Fan-voted Hall of Fame with AI cards + NFT auctions",
    live: false,
    img: "/projects/baseball-hall.png",
    year: "2025",
    skills: ["Product Design", "AI Integration", "Web3 / NFT", "Full-stack Development"],
    brief:
      "Baseball Hall is a fun fantasy sports platform where fans get to vote for their favourite players to enter a fan-voted Hall of Fame. We built a lively site featuring an embedded AI image generator that creates awesome player cards on the spot. We also added a tiered subscription system so fans can cast more votes at higher membership levels, plus a full NFT generation and auction system: for every player successfully enshrined, one NFT goes to the player or their family, one stays with Baseball Hall, and the third is auctioned to passionate fans.",
    highlights: [
      "Engaging fan-voting experience with real-time excitement",
      "Built-in AI player card generator for instant fun",
      "Smart subscription tiers and seamless NFT minting + auction flow",
    ],
    gallery: [2, 3, 4, 5, 6, 7, 8, 9].map((n) => `/projects/baseball/${n}.png`),
  },
];

export function getProject(id) {
  return PROJECTS.find((p) => p.id === id);
}

export function getNextProject(id) {
  const idx = PROJECTS.findIndex((p) => p.id === id);
  if (idx === -1) return PROJECTS[0];
  return PROJECTS[(idx + 1) % PROJECTS.length];
}
